import { useAuth0 } from '@auth0/auth0-react';
import Hotjar from '@hotjar/browser';
import { Toaster } from '@luminara/ui-components';
import { usePostHog } from 'posthog-js/react';
import React, { Suspense, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  createRoutesFromChildren,
  matchRoutes,
  Route,
  Routes,
  useLocation,
  useNavigationType,
  useParams
} from 'react-router-dom';
import { APP_ENV, HOTJAR_SITE_ID, HOTJAR_VERSION, SENTRY_DNS } from '../constants/app-constants';
import { instantiateWebsocket } from '../core/usecases/shared/ws/instantiate-websocket';
import AnalyticsEventUtil from '../infra/analytics/analytics-event-util';
import { addAuthorizationInterceptor } from '../infra/http/axios/interceptors/authorization.interceptor';
import LuminaraComponentWrapper from '../infra/luminara-component-wrapper';
import useUserDeviceAndScreenOrientationDetection from '../infra/screen/device-and-screen-orientation-detection';
import { GlobalAction } from '../store/reducers/global.reducer';
import {
  CLIENT_DETAILS_ROUTE,
  HEALER_AVAILABILITY_ROUTE,
  HEALER_COLOR_PLAYGROUND_ROUTE,
  HEALER_DASHBOARD_ROUTE,
  HEALER_MUSIC_PLAYGROUND_ROUTE,
  HEALER_PROFILE_ROUTE,
  HEALER_SCHEDULE_SESSION_ROUTE,
  HEALER_SESSIONS_ROUTE,
  HEALER_SETTINGS_ROUTE,
  selectCurrentUser,
  HEALER_BANK_DETAILS_ROUTE,
  setLoggedUserProfileId,
} from '../store/selectors/global.selector';
import { hideLoader, loaderSelector, showLoader } from '../store/selectors/loading-spinner-selector';
import NavbarContainer from './components/UI/navbar/NavbarContainer';
import LoaderSpinner from './components/UI/spinner/loader-spinner';
import BankDetailsContainer from './components/healer-bank-details/BankDetailsContainer';
import * as Sentry from "@sentry/react";

const ReactLazyPreload = (importStatement: () => Promise<{ default: React.ComponentType<any> }>) => {
  const Component = React.lazy(importStatement);
  (Component as any).preload = importStatement;
  return Component;
};

const BookingRescheduledSuccessfullyContainer = React.lazy(() => import('./components/booking/reschedule/BookingRescheduledSuccessfullyContainer'));
const RescheduleBookingScreen = React.lazy(() => import('./components/booking/reschedule/RescheduleBookingScreen'));
const BookingScheduledSuccessfullyContainer = React.lazy(() => import('./components/booking/schedule/BookingScheduledSuccessfullyContainer'));
const ScheduleBookingScreen = React.lazy(() => import('./components/booking/schedule/ScheduleBookingScreen'));
const ClientBookedAppointmentsScreen = React.lazy(() => import('./components/client-navigation/booked-appointments/ClientBookedAppointmentsScreen'));
const ClientSettingsContainer = React.lazy(() => import('./components/client-navigation/client-settings/ClientSettingsContainer'));
const MyHealersScreen = React.lazy(() => import('./components/my-healers/MyHealersScreen'));
const ClientDashboard = React.lazy(() => import('./components/dashboard/client/client-dashboard'));
const HealerDashboard = React.lazy(() => import('./components/dashboard/healer/healer-dashboard'));
const MusicPlaygroundScreen = React.lazy(() => import('./components/dashboard/healer/healer-playground/MusicPlaygroundScreen'));
const ColorPlaygroundScreen = React.lazy(() => import('./components/dashboard/healer/healer-playground/ColorPlaygroundScreen'));
const HealerFeedback = React.lazy(() => import('./components/feedback/healer-feedback'));
const HealerNoShowFeedback = React.lazy(() => import('./components/feedback/healer-noshow-feedback'));
const HealerAvailability = React.lazy(() => import('./components/healer-availability/HealerAvailability'));
const HealerCalendarIntegration = React.lazy(() => import('./components/healer-availability/HealerCalendarIntegration'));
const HealerInfoScreen = React.lazy(() => import('./components/healer-info/HealerInfoScreen'));
const HealerProfileContainer = React.lazy(() => import('./components/my-healers/my-healer-profile/MyHealerProfileContainer'));
const HealerScheduleBooking = React.lazy(() => import('./components/healer-schedule-booking/HealerScheduleBooking'));
const HealerSelectionContainer = React.lazy(() => import('./components/healer-selection/HealerSelectionContainer'));
const HealerSelectionPreviewContainer = React.lazy(() => import('./components/preview-healer-profile/HealerProfilePreviewContainer'));
const HealerSessionsScreen = React.lazy(() => import('./components/healer-sessions/HealerSessionsScreen'));
const ClientDetailsScreen = React.lazy(() => import('./components/healer-sessions/client-information/ClientDetailsScreen'));
const HealerSettingsScreen = React.lazy(() => import('./components/healer-settings/HealerSettingsScreen'));
const Heallo = React.lazy(() => import('./components/heallo/Heallo'));
const LoginScreenContainer = React.lazy(() => import('./components/login/LoginScreenContainer'));
const PaymentReturnScreenForMultipleBookings = React.lazy(() => import('./components/payment/multiple-bookings/PaymentReturnScreenForMultipleBookings'));
const PaymentScreenForMultipleBookings = React.lazy(() => import('./components/payment/multiple-bookings/PaymentScreenForMultipleBookings'));
const PaymentSuccessScreenForMultipleBookings = React.lazy(() => import('./components/payment/multiple-bookings/PaymentSuccessScreenForMultipleBookings'));
const PaymentReturnScreenForSingleBooking = React.lazy(() => import('./components/payment/single-booking/PaymentReturnScreenForSingleBooking'));
const PaymentScreenForSingleBooking = React.lazy(() => import('./components/payment/single-booking/PaymentScreenForSingleBooking'));
const PrivacyPolicy = React.lazy(() => import('./components/policies/PrivacyPolicy'));
const TermsOfUse = React.lazy(() => import('./components/policies/TermsOfUse'));
const PreSession = React.lazy(() => import('./components/pre-session/curl-line-animation/PreSession'));
const RequestNewHealerContainer = React.lazy(() => import('./components/request-new-healer/request-new-healer-container'));
export const WaitForHealsRecommendation = ReactLazyPreload(() => import('./components/request-new-healer/end/request-new-healer-end'));
const ClientRebookScreen = React.lazy(() => import('./components/session/meeting/client-rebook/ClientRebookScreen'));
const MeetingContainer = React.lazy(() => import('./components/session/meeting/meeting-container'));
const LocationListener = React.lazy(() => import('./location-handler'));
const MySelectionsScreen = React.lazy(() => import('./components/my-selections/my-selections-screen'));
const MySelectionsDetailScreen = React.lazy(() => import('./components/my-selections/my-selections-detail-screen'));
const ClientSessionFeedbackScreen = React.lazy(() => import('./components/client-session-feedback/client-session-feedback'));

Sentry.init({
  dsn: SENTRY_DNS,
  sendDefaultPii: true,
  tracePropagationTargets: ["*"],
  integrations: [
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
    }),
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    Sentry.consoleLoggingIntegration({ levels: [] }),
  ],
  tracesSampleRate: 1.0,
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 1.0,
  _experiments: { enableLogs: true },
  environment: APP_ENV,
});

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

export const App: React.FC = () => {
  const { getAccessTokenSilently, loginWithRedirect, isLoading } = useAuth0();
  const loading = useSelector(loaderSelector);
  const currentUser = useSelector(selectCurrentUser);
  const dispatch = useDispatch();
  const featureFlagProvider = usePostHog();

  useEffect(() => {
    if (HOTJAR_SITE_ID) {
      Hotjar.init(HOTJAR_SITE_ID, HOTJAR_VERSION);
    }
  }, []);

  useUserDeviceAndScreenOrientationDetection();

  useEffect(() => {
    if (currentUser?.id != null) {
      featureFlagProvider?.identify(currentUser?.id);
      AnalyticsEventUtil.getInstance().identify(currentUser?.id);
      Hotjar.isReady() && Hotjar.identify(currentUser?.id, {});
    }
  }, [currentUser, featureFlagProvider]);

  useEffect(() => {
    dispatch<any>(instantiateWebsocket());
    setViewportHeight();
    const timeZone = 'UTC';
    Intl.DateTimeFormat(undefined, { timeZone });
  }, [dispatch]);

  useEffect(() => {
    const handleResize = () => {
      AnalyticsEventUtil.getInstance().windowResized(window.innerHeight, window.innerWidth);
      setViewportHeight();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [dispatch]);

  useEffect(() => {
    const handleFirstUserInteraction = () => {
      dispatch({ type: 'SET_USER_HAS_INTERACTED', payload: true } as GlobalAction);
      document.removeEventListener('click', handleFirstUserInteraction);
      document.removeEventListener('touchstart', handleFirstUserInteraction);
    };

    document.addEventListener('click', handleFirstUserInteraction);
    document.addEventListener('touchstart', handleFirstUserInteraction);
    return () => {
      document.removeEventListener('click', handleFirstUserInteraction);
      document.removeEventListener('touchstart', handleFirstUserInteraction);
    };
  }, [dispatch]);

  useEffect(() => {
    if (isLoading) {
      dispatch(showLoader());
    } else {
      dispatch(hideLoader());
    }
  }, [dispatch, isLoading]);

  useEffect(() => {
    if (getAccessTokenSilently) {
      addAuthorizationInterceptor(dispatch, loginWithRedirect, getAccessTokenSilently);
    }
  }, [dispatch, getAccessTokenSilently, loginWithRedirect]);

  return (
    <>
      {loading === true && (
        <div data-testid="loader-container">
          <LoaderSpinner data-testid="loader" />
        </div>
      )}
      <NavbarContainer />
      <LocationListener />
      <Toaster />

      <Suspense fallback={<LoaderSpinner />}>
        <SentryRoutes>
          <Route path="/" element={<LuminaraComponentWrapper element={<LoginScreenContainer />} />} />
          <Route path="/login" element={<LuminaraComponentWrapper element={<LoginScreenContainer />} />} />
          <Route path="/privacy-policy" element={<LuminaraComponentWrapper element={<PrivacyPolicy />} />} />
          <Route path="/terms-of-use" element={<LuminaraComponentWrapper element={<TermsOfUse />} />} />

          {/* Admin Routes */}

          {/* Client Routes */}
          <Route path="/heallo/:bookingId" element={<LuminaraComponentWrapper element={<Heallo isHealer={false} />} />} />
          <Route path="/meeting/:bookingId" element={<LuminaraComponentWrapper element={<MeetingContainer isHealer={false} />} />} />
          <Route path="/rebook/:clientUserProfileId/:healerUserProfileId" element={<LuminaraComponentWrapper element={<ClientRebookScreen />} />} />

          <Route path="/dashboard/:clientUserProfileId" element={<LuminaraComponentWrapper element={<ClientDashboard />} />} />
          <Route path="/dashboard" element={<LuminaraComponentWrapper element={<ClientDashboard />} />} />
          <Route path="/pre-session/:bookingId" element={<LuminaraComponentWrapper element={<PreSession />} />} />

          <Route path="/booking/healer-selection/:clientUserProfileId" element={<LuminaraComponentWrapper element={<HealerSelectionContainer />} />} />

          <Route path="/booking/schedule/:clientUserProfileId/:packageId" element={<LuminaraComponentWrapper element={<ScheduleBookingScreen />} />} />

          <Route
            path="/booking/payment/:clientUserProfileId/:packageId/:bookingId"
            element={<LuminaraComponentWrapper element={<PaymentScreenForSingleBooking />} />}
          />
          <Route
            path="/booking/payment/:clientUserProfileId/:packageId/:bookingId/return"
            element={<LuminaraComponentWrapper element={<PaymentReturnScreenForSingleBooking />} />}
          />
          <Route
            path="/booking/schedule/:clientUserProfileId/:packageId/:bookingId/success"
            element={<LuminaraComponentWrapper element={<BookingScheduledSuccessfullyContainer />} />}
          />

          <Route
            path="/booking/multiple/payment/:clientUserProfileId/:packageId"
            element={<LuminaraComponentWrapper element={<PaymentScreenForMultipleBookings />} />}
          />
          <Route
            path="/booking/multiple/payment/:clientUserProfileId/:packageId/return"
            element={<LuminaraComponentWrapper element={<PaymentReturnScreenForMultipleBookings />} />}
          />
          <Route
            path="/booking/multiple/payment/:clientUserProfileId/:packageId/success"
            element={<LuminaraComponentWrapper element={<PaymentSuccessScreenForMultipleBookings />} />}
          />

          <Route
            path="/booking/reschedule/:clientUserProfileId/:packageId/:bookingId"
            element={<LuminaraComponentWrapper element={<RescheduleBookingScreen />} />}
          />
          <Route
            path="/booking/reschedule/:clientUserProfileId/:bookingId/success"
            element={<LuminaraComponentWrapper element={<BookingRescheduledSuccessfullyContainer />} />}
          />

          <Route path="/my-selections/:clientUserProfileId" element={<LuminaraComponentWrapper element={<MySelectionsScreen />} />} />
          <Route path="/my-selections/:clientUserProfileId/:healerRecommendationsId" element={<LuminaraComponentWrapper element={<MySelectionsDetailScreen />} />} />

          <Route path="/request-new-healer" element={<LuminaraComponentWrapper element={<RequestNewHealerContainer />} />} />
          <Route path="/request-new-healer/:clientUserProfileId" element={<LuminaraComponentWrapper element={<RequestNewHealerContainer />} />} />
          <Route path="/request-new-healer/:clientUserProfileId/:recommendationRequestId" element={<LuminaraComponentWrapper element={<WaitForHealsRecommendation />} />} />
          <Route path="/healer-selection/:healerUserProfileId/preview" element={<LuminaraComponentWrapper element={<HealerSelectionPreviewContainer />} />} />
          <Route path="/booked-appointments/:clientUserProfileId" element={<LuminaraComponentWrapper element={<ClientBookedAppointmentsScreen />} />} />
          <Route path="/my-healers/:clientUserProfileId" element={<LuminaraComponentWrapper element={<MyHealersScreen />} />} />
          <Route path="/my-healers/:clientUserProfileId/:healerUserProfileId" element={<LuminaraComponentWrapper element={<HealerProfileContainer />} />} />
          <Route path="/settings/:clientUserProfileId" element={<LuminaraComponentWrapper element={<ClientSettingsContainer title="Settings" />} />} />
          <Route path="/client-session-feedback/:clientUserProfileId" element={<LuminaraComponentWrapper element={<ClientSessionFeedbackScreen />} />} />

          {/* Healer routes */}
          <Route path="/healer/heallo/:bookingId" element={<LuminaraComponentWrapper element={<Heallo isHealer={true} />} />} />
          <Route path="/healer/meeting/:bookingId" element={<LuminaraComponentWrapper element={<MeetingContainer isHealer={true} />} />} />
          <Route path="/healer/feedback/:clientUserProfileId/:bookingId" element={<LuminaraComponentWrapper element={<HealerFeedback />} />} />
          <Route path="/healer/noshow-feedback/:bookingId" element={<LuminaraComponentWrapper element={<HealerNoShowFeedback />} />} />
          <Route path={HEALER_DASHBOARD_ROUTE} element={<LuminaraComponentWrapper element={<HealerDashboard />} />} />
          <Route path={HEALER_SCHEDULE_SESSION_ROUTE} element={<LuminaraComponentWrapper element={<HealerScheduleBooking />} />} />
          <Route path={HEALER_AVAILABILITY_ROUTE} element={<LuminaraComponentWrapper element={<HealerAvailability />} />} />
          <Route path="/healer/calendar/integrate" element={<LuminaraComponentWrapper element={<HealerCalendarIntegration />} />} />
          <Route path={HEALER_SESSIONS_ROUTE} element={<LuminaraComponentWrapper element={<HealerSessionsScreen />} />} />
          <Route path={HEALER_SETTINGS_ROUTE} element={<LuminaraComponentWrapper element={<HealerSettingsScreen />} />} />
          <Route path={`${CLIENT_DETAILS_ROUTE}/:clientUserProfileId/details`} element={<LuminaraComponentWrapper element={<ClientDetailsScreen />} />} />
          <Route path={HEALER_PROFILE_ROUTE} element={<LuminaraComponentWrapper element={<HealerInfoScreen />} />} />
          <Route path={HEALER_MUSIC_PLAYGROUND_ROUTE} element={<LuminaraComponentWrapper element={<MusicPlaygroundScreen />} />} />
          <Route path={HEALER_COLOR_PLAYGROUND_ROUTE} element={<LuminaraComponentWrapper element={<ColorPlaygroundScreen />} />} />
          <Route path={HEALER_BANK_DETAILS_ROUTE} element={<LuminaraComponentWrapper element={<BankDetailsContainer />} />} />
        </SentryRoutes>
      </Suspense>
    </>
  );
};

export default App;

function setViewportHeight() {
  const navbarHeightPx = 60;
  const vh = (window.innerHeight - navbarHeightPx) * 0.01;
  document.documentElement.style.setProperty('--vh-minus-navbar', `${vh}px`);
}
